import React, { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { hasSessionHint, setSessionHint } from '../utils/session'
import { getUsersMeCached } from '../utils/probeUsersMe'
import { clearTokens, isClientSessionPastHardExpiry } from '../utils/tokenManager'
import { getRedirectPathForUser } from '../utils/protectedRoutePolicy'
import { LISTING_CONTEXT_UPDATED_EVENT } from '../utils/currentListingContext'

function clearStoredUser() {
  try {
    localStorage.removeItem('user')
  } catch (_) {
    /* ignore */
  }
}

function readStoredUser() {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem('user')
    return raw ? JSON.parse(raw) : null
  } catch (_) {
    return null
  }
}

function getInitialProtectedState(currentPath) {
  if (typeof window === 'undefined') {
    return { authenticated: null, loginSearch: '', redirectPath: null }
  }
  if (isClientSessionPastHardExpiry()) {
    clearTokens()
    clearStoredUser()
    return { authenticated: false, loginSearch: 'session=expired', redirectPath: null }
  }

  const storedUser = readStoredUser()
  if (hasSessionHint() && storedUser) {
    return {
      authenticated: true,
      loginSearch: '',
      redirectPath: getRedirectPathForUser(currentPath, storedUser)
    }
  }

  return { authenticated: null, loginSearch: '', redirectPath: null }
}

/**
 * ProtectedRoute — session is validated via httpOnly cookie (GET /api/users/me).
 * Direct URL / refresh with an expired session always ends on login (with ?session=expired when applicable).
 */
const ProtectedRoute = ({ children }) => {
  const location = useLocation()
  const initialGate = useMemo(() => getInitialProtectedState(location.pathname), [location.pathname])
  const [isAuthenticated, setIsAuthenticated] = useState(initialGate.authenticated)
  const [loginSearch, setLoginSearch] = useState(initialGate.loginSearch)
  const [redirectPath, setRedirectPath] = useState(initialGate.redirectPath)

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const wasAuthenticated = isAuthenticated === true

      if (isClientSessionPastHardExpiry()) {
        clearTokens()
        clearStoredUser()
        if (!cancelled) {
          setLoginSearch('session=expired')
          setRedirectPath(null)
          setIsAuthenticated(false)
        }
        return
      }

      if (!cancelled) {
        setLoginSearch('')
        if (!wasAuthenticated) {
          setIsAuthenticated(null)
          setRedirectPath(null)
        }
      }

      const checkAuth = async () => {
        try {
          const hadClientSession = hasSessionHint() || !!readStoredUser()

          const me = await getUsersMeCached()
          if (!me.ok) {
            if (!cancelled) {
              clearTokens()
              clearStoredUser()
              setRedirectPath(null)
              setLoginSearch(hadClientSession ? 'session=expired' : '')
              setIsAuthenticated(false)
            }
            return
          }

          const data = me.json
          const parsedUser = data?.data
          if (!parsedUser) {
            if (!cancelled) {
              clearTokens()
              clearStoredUser()
              setRedirectPath(null)
              setLoginSearch(hadClientSession ? 'session=expired' : '')
              setIsAuthenticated(false)
            }
            return
          }

          setSessionHint()
          try {
            localStorage.setItem('user', JSON.stringify(parsedUser))
          } catch (e) {
            /* ignore */
          }

          if (!cancelled) {
            setLoginSearch('')
            setRedirectPath(getRedirectPathForUser(location.pathname, parsedUser))
            setIsAuthenticated(true)
          }
        } catch (error) {
          console.error('Error checking authentication:', error)
          if (!cancelled) {
            if (!wasAuthenticated) {
              // Network / unknown before the first successful auth probe.
              setRedirectPath(null)
              setLoginSearch('')
              setIsAuthenticated(false)
            }
          }
        }
      }

      await checkAuth()
    }

    run()
    return () => {
      cancelled = true
    }
  }, [location.pathname])

  useEffect(() => {
    if (isAuthenticated !== true) return undefined

    const recheckListingAccess = () => {
      const storedUser = readStoredUser()
      if (storedUser) {
        setRedirectPath(getRedirectPathForUser(location.pathname, storedUser))
      }
    }

    window.addEventListener(LISTING_CONTEXT_UPDATED_EVENT, recheckListingAccess)
    return () => window.removeEventListener(LISTING_CONTEXT_UPDATED_EVENT, recheckListingAccess)
  }, [location.pathname, isAuthenticated])

  if (isAuthenticated === null) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem' }}
      >
        <div
          className="spinner"
          style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2563eb',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite' }}
        />
        <p>Checking authentication...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  if (!isAuthenticated) {
    const returnPath = `${location.pathname}${location.search}${location.hash}`
    const params = new URLSearchParams(loginSearch || '')
    params.set('redirect', returnPath)
    const to = `/login?${params.toString()}`
    return <Navigate to={to} state={{ from: location }} replace />
  }

  if (redirectPath && location.pathname !== redirectPath) {
    return <Navigate to={redirectPath} replace />
  }

  return children
}

export default ProtectedRoute
